import { z } from 'zod';
import { uuidSchema, positiveInt, shortString, mediumString, } from './validation.js';
/**
 * Unified Audit Logs Schemas
 *
 * Shared validation and TypeScript types for the unified audit log feature.
 * These schemas are consumed by both backend (tRPC input validation) and
 * frontend (filter state, type inference).
 */
export const auditLogTypeSchema = z.enum(['order', 'inventory', 'webhook']);
export const auditLogStatusSchema = z.enum(['success', 'warning', 'failed']);
export const getAuditLogsSchema = z.object({
    types: z.array(auditLogTypeSchema).min(1).max(3).optional(),
    status: z.array(auditLogStatusSchema).min(1).max(3).optional(),
    locationId: uuidSchema.optional(),
    search: z.string().trim().max(120).optional(),
    startDate: z.string().datetime().optional(),
    endDate: z.string().datetime().optional(),
    page: positiveInt.default(1),
    pageSize: z.number().int().min(1).max(200).default(50),
});
export const auditLogEventSourceSchema = z.enum(['order', 'webhook', 'inventory']);
export const auditLogItemSchema = z.object({
    id: uuidSchema,
    type: auditLogTypeSchema,
    occurredAt: z.string().datetime(),
    action: shortString,
    status: auditLogStatusSchema.nullable().optional(),
    locationId: uuidSchema.nullable().optional(),
    orderId: uuidSchema.nullable().optional(),
    shopifyOrderId: z.number().int().min(0).nullable().optional(),
    sku: shortString.nullable().optional(),
    referenceType: shortString.nullable().optional(),
    referenceId: shortString.nullable().optional(),
    actorId: uuidSchema.nullable().optional(),
    actorName: shortString.nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).optional().default({}),
    notes: mediumString.nullable().optional(),
});
export const auditLogResponseSchema = z.object({
    totalCount: z.number().int().min(0),
    page: positiveInt,
    pageSize: z.number().int().min(1).max(200),
    items: z.array(auditLogItemSchema),
});
export const getOrderTimelineInputSchema = z
    .object({
    orderId: uuidSchema.optional(),
    shopifyOrderId: z.number().int().positive().optional(),
})
    .refine((value) => Boolean(value.orderId || value.shopifyOrderId), {
    message: 'orderId or shopifyOrderId must be provided',
});
export const orderTimelineEventSchema = z.object({
    id: shortString,
    occurredAt: z.string().datetime(),
    source: auditLogEventSourceSchema,
    label: shortString,
    description: mediumString.nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).optional().default({}),
});
export const orderTimelineResponseSchema = z.object({
    orderId: uuidSchema,
    shopifyOrderId: z.number().int().nullable(),
    events: z.array(orderTimelineEventSchema),
});
